/* Input park: the search input is a combobox and converts with the global search round (component-map.md). */
import { Combobox, ComboboxProps, useComboboxContext } from '@ariakit/react';
import {
  Center,
  forwardRef,
  Icon,
  IconButton as ChakraIconButton,
  IconProps,
  Input,
  InputGroup,
  InputLeftElement,
  InputProps,
  InputRightElement,
} from '@chakra-ui/react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Spinner } from '@main/complykit/components/spinner';
import { ReactNode, Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { useGlobalSearchContext } from './global-search-provider';

export interface GlobalSearchInputProps extends InputProps, Omit<ComboboxProps, keyof InputProps> {
  leftElement?(): ReactNode;
  rightElement?(): ReactNode;
  inputGroupRef?: Ref<HTMLDivElement>;
}

export const GlobalSearchInput = forwardRef<GlobalSearchInputProps, 'input'>(
  ({ leftElement, rightElement, inputGroupRef, size, ...inputProps }, ref) => {
    const { t } = useTranslation('ui');

    return (
      <InputGroup ref={inputGroupRef} size={size} w="full">
        <InputLeftElement pointerEvents="none">
          {leftElement?.() ?? <GlobalSearchIcon />}
        </InputLeftElement>

        <Input
          as={Combobox}
          ref={ref}
          size={size}
          fontSize="lg"
          variant="outline"
          border="0"
          focusBorderColor="transparent"
          placeholder={inputProps?.placeholder ?? t('globalSearch.placeholder')}
          _placeholder={{ color: 'gray.400' }}
          {...inputProps}
        />

        <InputRightElement w="auto">
          {rightElement?.() ?? <GlobalSearchInputRightElement />}
        </InputRightElement>
      </InputGroup>
    );
  },
);

export const GlobalSearchIcon = forwardRef<IconProps, 'svg'>((props, ref) => (
  <Icon ref={ref} w={6} h={6} as={MagnifyingGlassIcon} color="gray.400" {...props} />
));

export function GlobalSearchInputRightElement() {
  const globalSearch = useGlobalSearchContext();
  const combobox = useComboboxContext();

  // Show spinner in the input if we have cached results and loading new data in background
  if (globalSearch.isSearching && globalSearch.hasResults) {
    return (
      <Center width={10} color="var(--ck-text-muted)">
        <Spinner size="sm" />
      </Center>
    );
  }

  if (!combobox?.getState().value) {
    return null;
  }

  // Parked with the pickers round: the global search is a combobox, and its
  // clear converts with it rather than onto SearchField.
  return (
    <ChakraIconButton
      h="full"
      py={1}
      variant="link"
      icon={<Icon as={XMarkIcon} w={5} h={5} />}
      color="gray.400"
      aria-label="Clear search"
      onClick={() => combobox.setValue('')}
      onKeyDown={(e) => {
        if (e.code === 'Enter' || e.code === 'Space') {
          combobox.setValue('');
        }
      }}
    />
  );
}
